import Summary from '@modules/project/component/summary/Summary';
import type { ProjectPageLoader } from '@modules/project/type';
import {
    Accordion,
    AccordionContent,
    AccordionTrigger,
    AccordionItem,
} from '@rocco/components/accordion';
import { useFeature } from '~/modules/root';
import { useProjectPreviewRouteData } from '../../hook/use-project-preview-route-data';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { useState } from 'react';
import { ProjectBreadcrumb } from '../breadcrumb/Breadcrumb';
import { DetailsTableDesktop } from '../details/DetailsTableDesktop';
import { DetailsTable } from '@modules/project/component/details/DetailsTable';
import { ProjectTeam } from './ProjectTeam';

/**
 * Accordion component that displays different sections of project details including team members
 */

interface ProjectDetailsAccordionProps {
    projects: ProjectPageLoader['project'];
}

export const ProjectDetailsAccordion = ({
    projects,
}: ProjectDetailsAccordionProps) => {
    const project = useProjectPreviewRouteData();
    const showProjectTeam = useFeature('project_teams');
    const isAiHeroSummarizationEnabled = useFeature(
        'project_ai_hero_summarization'
    );

    const { openEnquiryModal } = useEnquiryModal({
        professional: projects.Professional,
        itemType: 'professional',
        siteTreeID: projects.ID,
        relatedItem: {
            id: projects.ID,
            type: 'project',
            title: projects.Title,
            category: projects.categoryPath,
        },
    });
    const [isSummaryOpen, setIsSummaryOpen] = useState(true);
    const [isProjectDetailsOpen, setIsProjectDetailsOpen] = useState(true);
    const [isProjectTeamOpen, setIsProjectTeamOpen] = useState(true);
    return (
        <div className="relative px-12 pt-10 md:px-15">
            <div className="hidden justify-end pb-2 md:flex">
                <ProjectBreadcrumb
                    breadcrumbs={[
                        { href: '/', label: 'Home' },
                        ...project.Breadcrumbs.map((b) => ({
                            href: b.href,
                            label: b.label,
                        })),
                    ]}
                />
            </div>
            <div>
                {project.Content ? (
                    <Accordion
                        type="multiple"
                        className="divide-y divide-gray-900 border-t border-t-gray-900/20"
                        defaultValue={['about']}
                    >
                        {makeAccordionItem({
                            key: 'about',
                            title: project.Title || 'About',
                            content: <Summary showButtons={false} />,
                            isOpen: isSummaryOpen,
                            setIsOpen: setIsSummaryOpen,
                            icon: 'x',
                            openIcon: 'plus',
                            variant: 'spacious',
                        })}
                    </Accordion>
                ) : null}
            </div>
            <div>
                {project.Content ? (
                    <Accordion
                        type="multiple"
                        className="divide-y divide-gray-900 border-t border-t-gray-900/20"
                        defaultValue={['project-details']}
                    >
                        {makeAccordionItem({
                            key: 'project-details',
                            title:
                                isAiHeroSummarizationEnabled &&
                                project.ProjectHeroTitle
                                    ? project.Title
                                    : 'Project Details',
                            content: (
                                <DetailsTableDesktop
                                    data={project}
                                    openEnquiryModal={openEnquiryModal}
                                />
                            ),
                            isOpen: isProjectDetailsOpen,
                            setIsOpen: setIsProjectDetailsOpen,
                            icon: 'x',
                            openIcon: 'plus',
                            variant: 'spacious',
                        })}
                    </Accordion>
                ) : null}
            </div>
            <div>
                {showProjectTeam && (
                    <Accordion
                        type="multiple"
                        className="divide-y divide-gray-900 border-t border-t-gray-900/20"
                        defaultValue={['project-team']}
                    >
                        {makeAccordionItem({
                            key: 'project-team',
                            title: 'Project Team',
                            content: (
                                <ProjectTeam
                                    projectID={projects.ID}
                                    openEnquiryModal={openEnquiryModal}
                                />
                            ),
                            isOpen: isProjectTeamOpen,
                            setIsOpen: setIsProjectTeamOpen,
                            icon: 'x',
                            openIcon: 'plus',
                            variant: 'spacious',
                        })}
                    </Accordion>
                )}
            </div>
            <div className="flex justify-end pb-3 md:hidden">
                <ProjectBreadcrumb
                    breadcrumbs={[
                        { href: '/', label: 'Home' },
                        ...project.Breadcrumbs.map((b) => ({
                            href: b.href,
                            label: b.label,
                        })),
                    ]}
                />
            </div>
            <Accordion
                type="multiple"
                className="block divide-y divide-gray-900 border-b border-t border-b-gray-900/20 border-t-gray-900/20 md:hidden"
                defaultValue={['project-details']}
            >
                {makeAccordionItem({
                    key: 'project-details',
                    title: 'Project Details',
                    content: (
                        <DetailsTable
                            data={project}
                            openEnquiryModal={openEnquiryModal}
                        />
                    ),
                })}
                {showProjectTeam &&
                    makeAccordionItem({
                        key: 'project-team',
                        title: 'Project Team',
                        content: (
                            <ProjectTeam
                                projectID={projects.ID}
                                openEnquiryModal={openEnquiryModal}
                            />
                        ),
                    })}
            </Accordion>
            <div className="hidden border-b border-b-gray-900/20 md:block" />
        </div>
    );
};

const makeAccordionItem = ({
    key,
    title,
    content,
    isOpen,
    setIsOpen,
    icon = 'plus',
    openIcon = 'minus',
    variant = 'default',
}: {
    key: string;
    title?: string;
    content: React.ReactNode;
    isOpen?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    icon?: 'plus' | 'minus' | 'x' | 'chevronDown' | 'chevronRight';
    openIcon?: 'plus' | 'minus' | 'x' | 'chevronDown' | 'chevronRight';
    variant?: 'default' | 'spacious';
}) => {
    return (
        <AccordionItem key={key} value={key} variant="noBorder">
            <AccordionTrigger
                icon={icon}
                openIcon={openIcon}
                isOpen={isOpen}
                onClick={() => setIsOpen?.(!isOpen)}
                variant={variant}
            >
                <span className="text-lg font-medium tracking-[0.025rem]">
                    {title}
                </span>
            </AccordionTrigger>
            <AccordionContent>{content}</AccordionContent>
        </AccordionItem>
    );
};
